/**
 * 统一管理页面
 * 整合：微服务发现、应用组件管理、应用数据集管理
 * 左侧树形结构：微服务 -> 页面 -> 组件 -> 子组件 -> 数据集
 */

import React, { useMemo, useState, useCallback } from 'react';
import { Layout, Empty, Dropdown, Menu, Modal, message } from 'antd';
import { MoreOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { ContainerPanel, ContainerModal, CompsForm } from '@ms/shared-components';
import UnifiedTree from './components/UnifiedTree';
import NodeDetailPanel from './components/NodeDetailPanel';
import AddNodeModal from './components/AddNodeModal';
import { useUnifiedManagement } from './hooks/useUnifiedManagement';
import { buildUnifiedTree } from './utils/treeBuilder';
import '../shared/ai-config.css';

const { Sider, Content } = Layout;

const UnifiedManagementPage: React.FC = () => {
  const {
    services,
    components,
    datasets,
    loading,
    selectedNode,
    expandedKeys,
    selectedKeys,
    addModalVisible,
    addModalType,
    addModalParent,
    detailModalVisible,
    editingNode,
    formRef,
    loadAllData,
    handleSelect,
    handleExpand,
    handleAddNode,
    handleAddSuccess,
    handleCloseAddModal,
    handleEditNode,
    handleSaveNode,
    handleDeleteNode,
    handleCloseDetailModal,
    setExpandedKeys,
  } = useUnifiedManagement();

  // 构建统一的树形数据
  const treeData = useMemo(() => {
    return buildUnifiedTree(services, components, datasets);
  }, [services, components, datasets]);

  // 树面板按钮
  const treeButtons = useMemo(
    () => [
      {
        key: 'refresh',
        icon: <PlusOutlined />,
        type: 'text' as const,
        onClick: loadAllData,
        title: '刷新',
        showTextOnHover: true,
      },
    ],
    [loadAllData]
  );

  return (
    <div style={{ height: '100vh', overflow: 'hidden' }}>
      <Layout style={{ height: '100%' }}>
        {/* 左侧：统一树形结构 */}
        <Sider
          width={400}
          style={{
            background: '#fff',
            borderRight: '1px solid #f0f0f0',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
          }}
        >
          <ContainerPanel title="统一管理树" buttons={treeButtons} height={'100%'}>
            <UnifiedTree
              treeData={treeData}
              selectedKeys={selectedKeys}
              expandedKeys={expandedKeys}
              onSelect={handleSelect}
              onExpand={handleExpand}
              onAddNode={handleAddNode}
              onEditNode={handleEditNode}
              onDeleteNode={handleDeleteNode}
              loading={loading}
            />
          </ContainerPanel>
        </Sider>

        {/* 右侧：节点详情面板 */}
        <Content
          style={{
            background: '#f0f2f5',
            padding: 24,
            overflow: 'auto',
          }}
        >
          <div style={{ minHeight: '100%' }}>
            <ContainerPanel title="节点详情" buttons={[]}>
              {selectedNode ? (
                <NodeDetailPanel
                  node={selectedNode}
                  onSave={handleSaveNode}
                  formRef={formRef}
                />
              ) : (
                <Empty
                  description="请从左侧选择要查看的节点"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </ContainerPanel>
          </div>
        </Content>
      </Layout>

      {/* 添加节点对话框 */}
      <AddNodeModal
        visible={addModalVisible}
        type={addModalType}
        parent={addModalParent}
        onSuccess={handleAddSuccess}
        onCancel={handleCloseAddModal}
      />

      {/* 节点详情编辑对话框 */}
      <ContainerModal
        title={editingNode ? `编辑${getNodeTypeLabel(editingNode.type)}` : '节点详情'}
        visible={detailModalVisible}
        onCancel={handleCloseDetailModal}
        width={600}
        height="auto"
        contentPadding={{ vertical: 24, horizontal: 16 }}
        footerRightButtons={[
          {
            key: 'cancel',
            text: '取消',
            type: 'default',
            onClick: handleCloseDetailModal,
          },
          {
            key: 'save',
            text: '保存',
            type: 'primary',
            onClick: handleSaveNode,
          },
        ]}
      >
        {editingNode && (
          <NodeDetailPanel
            node={editingNode}
            onSave={handleSaveNode}
            isModal={true}
            formRef={formRef}
          />
        )}
      </ContainerModal>
    </div>
  );
};

// 获取节点类型标签
const getNodeTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    service: '微服务',
    page: '页面',
    component: '组件',
    dataset: '数据集',
    field: '字段',
  };
  return labels[type] || type;
};

export default UnifiedManagementPage;
